import React, { useState, useCallback, lazy, Suspense } from 'react';
import SealogEventTimeline from './sealogEventTimeline';
import { RealAssetDeploymentWithChildrenResponse } from '../../apiClient';
import { EventDetails, TimelineItem } from "../timeline/types";
import { getEnv } from "../../utils/env";

const Map = lazy(() => import("../map"));

const MapWithRovDives = lazy(() => import("../mockROVComponents/mapWithRovDives"));

interface SealogEventTrackerProps {
    deployment: RealAssetDeploymentWithChildrenResponse;
    deploymentPublicId: string;
}

const SealogEventTracker: React.FC<SealogEventTrackerProps> = ({
    deployment,
    deploymentPublicId
}) => {
    const [hasSealogEvents, setHasSealogEvents] = useState<boolean | null>(null);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
    const [mapLocation, setMapLocation] = useState<{ lon: number, lat: number } | null>(null);
    const [pinnedEventDetail, setPinnedEventDetail] = useState<EventDetails | null>(null);
    const [timelineItemsById, setTimelineItemsById] = useState<Record<string, TimelineItem>>({});

    const [shouldFlash, setShouldFlash] = useState(false);
    const handleEndFlash = useCallback(() => {
        setShouldFlash(false);
    }, []);

    const [allFilteredEventIds, setAllFilteredEventIds] = useState<Set<string>>(new Set());
    const [filtersApplied, setFiltersApplied] = useState(false);

    const handleDataLoaded = (total: number) => {
        setHasSealogEvents(total > 0);
    };

    const handleAllFilteredEventIdsChanged = useCallback(
        (publicIds: Set<string>, isFiltersApplied: boolean) => {
            setAllFilteredEventIds(prev => {
                if (prev.size === publicIds.size) {
                    let identical = true;
                    publicIds.forEach(id => {
                        if (!prev.has(id)) {
                            identical = false;
                            return false;
                        }
                    });
                    if (identical && filtersApplied === isFiltersApplied) {
                        return prev;
                    }
                }
                setFiltersApplied(isFiltersApplied);
                return publicIds;
            });
        },
        [filtersApplied]
    );

    const handleMapEventSelect = useCallback((publicId: string) => {
        setSelectedEventId(publicId);
        setShouldFlash(true);
    }, []);

    const handleEventSelect = (
        eventId: string,
        lon?: number,
        lat?: number,
        eventDetails?: EventDetails
    ) => {
        setShouldFlash(false);
        if (selectedEventId === eventId) {
            setSelectedEventId(null);
            setMapLocation(null);
            setPinnedEventDetail(null);
        } else {
            setSelectedEventId(eventId);

            if (lon !== undefined && lat !== undefined) {
                setMapLocation({ lon, lat });
            }

            if (eventDetails) {
                setPinnedEventDetail(eventDetails);
            }
        }
    };

    const handleEventHover = useCallback((eventId: string | null) => {
        setHoveredEventId(eventId);
    }, []);

    const deploymentIsAzores2023 =
        ((getEnv().NEXT_PUBLIC_ENV === "dev" || getEnv().NEXT_PUBLIC_ENV === "stg") && deploymentPublicId === "7d3becde-ca9b-4721-a179-b5ee0904d21e") ||
        (getEnv().NEXT_PUBLIC_ENV === "prod" && deploymentPublicId === "73bdd43a-a583-45c1-ae9a-7f8a9a6b64a9");

    return (
        <>
            <div className="mt-9">
                <p className="mb-2 text-2xl text-blue-100">
                    {hasSealogEvents ? "Sealog Event Tracker" : "Deployment Trackline"}
                </p>
            </div>

            <div className={`grid ${hasSealogEvents ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
                <div className='h-[525px] 2xl:h-[750px]'>
                  <Suspense fallback={<div>Loading map...</div>}>
                    {deploymentIsAzores2023 ? (
                        <MapWithRovDives
                            deployments={[deployment]}
                            selectedDeploymentIds={[deployment.external_id] as any}
                            currentPageProp={1}
                            pageSizeProp={1}
                            showInfoPanel={false}
                            selectedEventId={selectedEventId as any}
                            hoveredEventId={hoveredEventId as any}
                        />
                    ) : (
                        <Map
                            deployments={[deployment]}
                            selectedDeploymentIds={[deployment.external_id] as any}
                            currentPageProp={1}
                            pageSizeProp={1}
                            showInfoPanel={false}
                            selectedEventId={selectedEventId as any}
                            hoveredEventId={hoveredEventId as any}
                            mapLocation={mapLocation}
                            pinnedEventDetail={pinnedEventDetail}
                            timelineItemsById={timelineItemsById}
                            onEventHover={handleEventHover}
                            onEventSelect={handleMapEventSelect}
                            filtersApplied={filtersApplied}
                            allFilteredEventIds={allFilteredEventIds}
                            onEventDeselect={() => {
                                setSelectedEventId(null);
                                setMapLocation(null);
                                setPinnedEventDetail(null);
                            }}
                        />
                    )}
                  </Suspense>
                </div>

                <div className={hasSealogEvents ? "h-[525px] 2xl:h-[650px]" : "hidden"}>
                    <SealogEventTimeline
                        realAssetDeploymentPublicId={deploymentPublicId}
                        onDataLoaded={handleDataLoaded}
                        deployment={deployment}
                        selectedEventId={selectedEventId}
                        onEventSelect={handleEventSelect}
                        onEventHover={handleEventHover}
                        onAllTimelineItemsUpdated={(dict) => setTimelineItemsById(dict)}
                        cardShouldFlash={shouldFlash}
                        onEndFlash={handleEndFlash}
                        onAllFilteredEventIdsChanged={handleAllFilteredEventIdsChanged}
                    />
                </div>
            </div>
        </>
    );
};

export default SealogEventTracker;
